import { $fetch } from 'ofetch'
import type { ImageRecord } from '../utils/imageDb'
import { wasRemoteDeleted } from './uploadService'

export type RemoteProjectUpload = {
  id: string
  assetId?: string | null
  source?: string | null
  editorReadyAt?: string | null
  categoryId: number
  productId?: number | null
  meta?: Record<string, any> | null
  name: string
  mime?: string | null
  bytes?: number | null
  width?: number | null
  height?: number | null
  createdAt?: string | null
  previewUrl?: string | null
  thumbUrl?: string | null
  fullscreenPreviewUrl?: string | null
  status?: string | null
  processing?: boolean | null
  remoteUrl: string
}

type RemoteRecordOptions = {
  projectCode?: string | null
}

export const OPEN_REMOTE_UPLOAD_STATUSES = ['presigned', 'uploaded', 'processing'] as const

const projectUploadsInflight = new Map<string, Promise<RemoteProjectUpload[]>>()
const projectUploadsCache = new Map<string, { ts: number; items: RemoteProjectUpload[] }>()
const PROJECT_UPLOADS_CACHE_TTL_MS = 5000
const PROJECT_UPLOADS_TIMEOUT_MS = 30_000

// Cache keys are JSON produced by projectUploadsRequestKey(). Match on the parsed
// structured fields instead of substring-matching the raw JSON, which was fragile
// (a base URL/project code value containing the marker substring could match wrongly).
function cacheKeyMatches(key: string, base: string, code: string): boolean {
  if (!base && !code) return true
  let parsed: any
  try {
    parsed = JSON.parse(key)
  } catch {
    return false
  }
  if (base && String(parsed?.editorBaseUrl || '') !== base) return false
  if (code && String(parsed?.projectCode || '') !== code) return false
  return true
}

export function invalidateProjectUploadsCache(opts?: {
  editorBaseUrl?: string
  projectCode?: string
}) {
  if (!opts?.editorBaseUrl && !opts?.projectCode) {
    projectUploadsCache.clear()
    projectUploadsInflight.clear()
    return
  }

  const base = String(opts?.editorBaseUrl || '').replace(/\/+$/, '')
  const code = String(opts?.projectCode || '').trim()
  for (const key of [...projectUploadsCache.keys()]) {
    if (!cacheKeyMatches(key, base, code)) continue
    projectUploadsCache.delete(key)
  }
  for (const key of [...projectUploadsInflight.keys()]) {
    if (!cacheKeyMatches(key, base, code)) continue
    projectUploadsInflight.delete(key)
  }
}

export function upsertProjectUploadsCacheItem(
  item: RemoteProjectUpload,
  opts?: {
    editorBaseUrl?: string
    projectCode?: string
  },
) {
  const id = String(item?.id || '').trim()
  if (!id) return

  const base = String(opts?.editorBaseUrl || '').replace(/\/+$/, '')
  const code = String(opts?.projectCode || '').trim()

  for (const [key, entry] of [...projectUploadsCache.entries()]) {
    if (!cacheKeyMatches(key, base, code)) continue

    const nextItems = [...(entry?.items || [])]
    const index = nextItems.findIndex((existing) => String(existing?.id || '').trim() === id)
    if (index >= 0) {
      nextItems[index] = {
        ...nextItems[index],
        ...item,
      }
    } else {
      nextItems.unshift(item)
    }
    projectUploadsCache.set(key, { ts: Date.now(), items: nextItems })
  }
}

function projectUploadsRequestKey(opts: {
  editorBaseUrl: string
  projectCode: string
  categoryId?: number | null
  productId?: number | null
  since?: string | null
  source?: string | null
  editorReady?: boolean | null
  statuses?: string[] | null
  limit?: number
  offset?: number
}) {
  return JSON.stringify({
    editorBaseUrl: String(opts.editorBaseUrl || '').replace(/\/+$/, ''),
    projectCode: String(opts.projectCode || '').trim(),
    categoryId: Number(opts.categoryId || 0) || 0,
    productId: Number(opts.productId || 0) || 0,
    since: String(opts.since || ''),
    source: String(opts.source || ''),
    editorReady: opts.editorReady == null ? '' : (opts.editorReady ? '1' : '0'),
    statuses: Array.isArray(opts.statuses) ? opts.statuses.map((x) => String(x || '').trim()).filter(Boolean).sort() : [],
    limit: Number(opts.limit ?? 100),
    offset: Math.max(0, Number(opts.offset || 0)),
  })
}

export async function fetchProjectUploads(opts: {
  editorBaseUrl: string
  projectCode: string
  categoryId?: number | null
  productId?: number | null
  since?: string | null
  source?: string | null
  editorReady?: boolean | null
  statuses?: string[] | null
  limit?: number
  offset?: number
}): Promise<RemoteProjectUpload[]> {
  const key = projectUploadsRequestKey(opts)
  const now = Date.now()
  const cached = projectUploadsCache.get(key)
  if (cached && now - cached.ts < PROJECT_UPLOADS_CACHE_TTL_MS) {
    return cached.items
  }

  const running = projectUploadsInflight.get(key)
  if (running) return running

  const base = String(opts.editorBaseUrl || '').replace(/\/+$/, '')
  const query: Record<string, any> = {
    projectCode: opts.projectCode,
    limit: opts.limit ?? 100,
    offset: Math.max(0, Number(opts.offset || 0)),
  }

  if (opts.categoryId && opts.categoryId > 0) query.categoryId = opts.categoryId
  if (opts.productId && opts.productId > 0) query.productId = opts.productId
  if (opts.since) query.since = opts.since
  if (opts.source) query.source = opts.source
  if (opts.editorReady != null) query.editorReady = opts.editorReady ? '1' : '0'
  if (Array.isArray(opts.statuses) && opts.statuses.length) query.status = opts.statuses.join(',')

  // Timeout guards against a hung request blocking every dedup-joined caller.
  // The .finally below always clears the inflight entry, including on rejection.
  const request = $fetch(`${base}/upload/list`, { query, timeout: PROJECT_UPLOADS_TIMEOUT_MS })
    .then((res: any) => {
      const items = Array.isArray(res?.items) ? res.items : []
      const filtered = items.filter((item: any) => !wasRemoteDeleted(String(item?.id || '')))
      projectUploadsCache.set(key, { ts: Date.now(), items: filtered })
      return filtered
    })
    .finally(() => {
      projectUploadsInflight.delete(key)
    })

  projectUploadsInflight.set(key, request)
  return request
}

export async function importRemoteUploadToRecord(item: RemoteProjectUpload): Promise<ImageRecord> {
  if (wasRemoteDeleted(String(item?.id || ''))) {
    throw new Error('remote_upload_deleted')
  }
  return mapRemoteUploadToRecordMeta(item)
}

export function mapRemoteUploadToRecordMeta(item: RemoteProjectUpload, opts?: RemoteRecordOptions): ImageRecord {
  const createdAtMs = item.createdAt ? Date.parse(item.createdAt) : Date.now()
  const editorReadyAtMs = item.editorReadyAt ? Date.parse(item.editorReadyAt) : NaN
  const previewUrl = String(item.thumbUrl || item.previewUrl || '').trim() || undefined
  const fullscreenPreviewUrl = String(item.fullscreenPreviewUrl || '').trim() || undefined
  const remoteUrl = String(item.remoteUrl || '').trim()
  const state = String(item.status || (item.processing ? 'processing' : 'done') || 'done').trim() || 'done'
  const projectCode = String(opts?.projectCode || '').trim() || undefined

  return {
    id: item.id,
    assetId: item.assetId ?? null,
    name: String(item.name || item.id),
    createdAt: Number.isFinite(createdAtMs) ? createdAtMs : Date.now(),
    updatedAt: Date.now(),
    mime: String(item.mime || 'image/jpeg'),
    bytes: Number(item.bytes || 0),
    width: item.width != null ? Number(item.width) : undefined,
    height: item.height != null ? Number(item.height) : undefined,
    categoryId: Number(item.categoryId || 0),
    productId: item.productId != null ? Number(item.productId || 0) : null,
    ...(projectCode ? { projectCode } : {}),
    ...(item.meta && typeof item.meta === 'object'
      ? { edits: ((item.meta as any)?.edits || undefined) as any }
      : {}),
    ...(item.meta && typeof item.meta === 'object' && Array.isArray((item.meta as any)?.tags) && (item.meta as any).tags.length
      ? { tags: (item.meta as any).tags.map((t: unknown) => String(t)) }
      : {}),
    upload: {
      state,
      phase: state,
      progressPct: state === 'done' ? 100 : 0,
      uploadedBytes: Number(item.bytes || 0),
      totalBytes: Number(item.bytes || 0),
      ...(previewUrl ? { previewUrl, thumbUrl: previewUrl } : {}),
      ...(fullscreenPreviewUrl ? { fullscreenPreviewUrl } : {}),
      remoteUrl,
      ...(item.source && ['desktop', 'phone', 'stock'].includes(String(item.source)) ? { source: String(item.source) as 'desktop' | 'phone' | 'stock' } : {}),
      ...(item.meta && typeof item.meta === 'object' && String((item.meta as any)?.deviceClass || '').trim()
        ? { deviceClass: String((item.meta as any).deviceClass).trim() as any }
        : {}),
      ...(item.meta && typeof item.meta === 'object' && String((item.meta as any)?.deviceOs || '').trim()
        ? { deviceOs: String((item.meta as any).deviceOs).trim() }
        : {}),
      ...(item.meta && typeof item.meta === 'object' && String((item.meta as any)?.deviceBrowser || '').trim()
        ? { deviceBrowser: String((item.meta as any).deviceBrowser).trim() }
        : {}),
      ...(Number.isFinite(editorReadyAtMs) ? { editorReadyAt: Number(editorReadyAtMs) } : {}),
      tries: 1,
      updatedAt: Date.now(),
    },
  } as ImageRecord
}

/**
 * Lokale Datensätze, die fertig hochgeladen waren, auf dem Server aber fehlen –
 * z. B. weil die Position im Warenkorb der Website gelöscht wurde, während kein
 * Editor-Tab offen war (Mercure erreicht nur offene Tabs).
 *
 * Nur verwenden, wenn `serverIds` die VOLLSTÄNDIGE Projektliste ist (Request ok,
 * weniger Treffer als limit). Laufende/fehlgeschlagene Uploads und frisch fertig
 * gewordene Datensätze (Gnadenfrist gegen gecachte Listen) bleiben unangetastet.
 */
export function findServerDeletedRecords(
  local: ImageRecord[],
  serverIds: Set<string>,
  projectCode: string,
  graceMs = 120_000,
): ImageRecord[] {
  const code = String(projectCode || '').trim()
  if (!code) return []
  const cutoff = Date.now() - graceMs
  return local.filter((rec) => {
    const id = String(rec?.id || '').trim()
    if (!id || serverIds.has(id)) return false
    if (String(rec?.projectCode || '').trim() !== code) return false
    const upload = rec?.upload
    if (!upload || String(upload.state || '') !== 'done') return false
    if (!String(upload.remoteUrl || upload.fullscreenPreviewUrl || upload.previewUrl || '').trim()) return false
    const touchedAt = Math.max(Number(upload.updatedAt || 0), Number(rec.updatedAt || 0))
    return touchedAt > 0 && touchedAt < cutoff
  })
}

export function mergeServerAndLocalImageRecord(server: ImageRecord, local?: ImageRecord | null): ImageRecord {
  if (!local) return server

  const serverPreviewUrl = String((server as any)?.upload?.previewUrl || '').trim()
  const serverThumbUrl = String((server as any)?.upload?.thumbUrl || '').trim()
  const serverFullscreenPreviewUrl = String((server as any)?.upload?.fullscreenPreviewUrl || '').trim()
  const preferServerThumbs = !!(serverPreviewUrl || serverThumbUrl || serverFullscreenPreviewUrl)

  const mergedPreset = {
    ...((((server as any)?.edits?.preset || {}) as any)),
    ...((((local as any)?.edits?.preset || {}) as any)),
  }
  const mergedEdits = {
    ...(((server as any)?.edits || {}) as any),
    ...(((local as any)?.edits || {}) as any),
    preset: Object.keys(mergedPreset).length ? mergedPreset : undefined,
  }

  // Lokal fertig hochgeladen, Server noch bei der Nachverarbeitung (Vorschaubilder,
  // nur ein Worker): den lokalen Fertig-Status nicht zurückstufen. Sonst zeigt die
  // Kachel nach jedem Teil-Reload (z. B. Option „Weißer Rand“ für mehrere Bilder)
  // wieder „Wird hochgeladen“, bis die Seite neu geladen wird.
  const serverUpload = ((server as any)?.upload || {}) as any
  const localUpload = ((local as any)?.upload || {}) as any
  const serverState = String(serverUpload.state || '').trim()
  const localDone = String(localUpload.state || '').trim() === 'done'
    || String(localUpload.phase || '').trim() === 'done'
    || Number(localUpload.editorReadyAt || 0) > 0
  const keepLocalDone = localDone && serverState !== 'done' && serverState !== 'failed'

  return {
    ...(server as any),
    ...(!String((server as any)?.name || '').trim() && local?.name ? { name: local.name } : {}),
    createdAt: Number(server?.createdAt || 0) || Number(local?.createdAt || 0) || Date.now(),
    updatedAt: Math.max(Number(server?.updatedAt || 0), Number(local?.updatedAt || 0), Date.now()),
    ...(local?.width != null ? { width: Number(local.width) } : {}),
    ...(local?.height != null ? { height: Number(local.height) } : {}),
    ...(local?.editorWidth != null ? { editorWidth: Number((local as any).editorWidth) } : {}),
    ...(local?.editorHeight != null ? { editorHeight: Number((local as any).editorHeight) } : {}),
    ...(local?.thumbBlob ? { thumbBlob: local.thumbBlob } : {}),
    ...(local?.thumbWidth != null ? { thumbWidth: Number((local as any).thumbWidth) } : {}),
    ...(local?.thumbHeight != null ? { thumbHeight: Number((local as any).thumbHeight) } : {}),
    ...(local?.productId != null ? { productId: Number(local.productId || 0) || null } : {}),
    ...(local?.categoryId != null ? { categoryId: Number(local.categoryId || 0) || null } : {}),
    ...(((local as any)?.parentCategoryId != null) ? { parentCategoryId: Number((local as any).parentCategoryId || 0) || null } : {}),
    ...(((local as any)?.productTitle) ? { productTitle: String((local as any).productTitle) } : {}),
    ...(((local as any)?.categoryTitle) ? { categoryTitle: String((local as any).categoryTitle) } : {}),
    ...(((local as any)?.parentCategoryTitle) ? { parentCategoryTitle: String((local as any).parentCategoryTitle) } : {}),
    ...(preferServerThumbs ? {
      // Drop the local preview BLOBS (server thumbnails take over), but keep the
      // editor DIMENSIONS — editorWidth/editorHeight describe the crop's source
      // pixel space and are needed for correct crop restore on reload. Wiping them
      // forced editorDimsForRecord to fall back to the full-res width and produced
      // wrong crops.
      thumbBlob: undefined,
      thumbWidth: undefined,
      thumbHeight: undefined,
      editorBlob: undefined,
    } : {}),
    ...(Object.keys(mergedEdits).length ? { edits: mergedEdits } : {}),
    upload: {
      ...serverUpload,
      ...(keepLocalDone ? {
        state: 'done',
        phase: 'done',
        progressPct: 100,
        editorReadyAt: Number(serverUpload.editorReadyAt || 0) || Number(localUpload.editorReadyAt || 0) || Date.now(),
      } : {}),
      ...(!preferServerThumbs && ((local as any)?.upload?.previewUrl || (local as any)?.upload?.thumbUrl)
        ? {
            previewUrl: (local as any)?.upload?.previewUrl ?? (server as any)?.upload?.previewUrl,
            thumbUrl: (local as any)?.upload?.thumbUrl ?? (server as any)?.upload?.thumbUrl,
          }
        : {}),
      ...(!preferServerThumbs && ((local as any)?.upload?.fullscreenPreviewUrl || (server as any)?.upload?.fullscreenPreviewUrl)
        ? {
            fullscreenPreviewUrl: (local as any)?.upload?.fullscreenPreviewUrl ?? (server as any)?.upload?.fullscreenPreviewUrl,
          }
        : {}),
    },
  } as ImageRecord
}
